import { Injectable } from '@angular/core';
import { writeBatch } from '@angular/fire/firestore';
import { firstValueFrom, map, Observable, shareReplay, switchMap } from 'rxjs';
import { Sponsor } from '@models';
import { FirestoreService } from './firestore.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class SponsorsService {
  public all$: Observable<Sponsor[]>;

  constructor(
    private readonly firestore: FirestoreService,
    private readonly tenantContext: TenantContextService,
  ) {
    this.all$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<Sponsor>(
          this.tenantContext.collections.SPONSORS,
        ),
      ),
      map((sponsors) => [...sponsors].sort(this._compareOrder)),
      shareReplay(1),
    );
  }

  getById(id: string): Observable<Sponsor> {
    return this.firestore.getDocument<Sponsor>(
      this.tenantContext.collections.SPONSORS,
      id,
    );
  }

  async create(data: Partial<Sponsor>): Promise<string> {
    const current = await firstValueFrom(this.all$);
    const nextOrder =
      current.reduce((max, s) => Math.max(max, s.order ?? 0), -1) + 1;
    return this.firestore.create<Sponsor>(
      this.tenantContext.collections.SPONSORS,
      { order: nextOrder, ...data },
    );
  }

  async update(data: Partial<Sponsor> & { id: string }): Promise<void> {
    return this.firestore.update<Sponsor>(
      this.tenantContext.collections.SPONSORS,
      data,
    );
  }

  async delete(id: string): Promise<void> {
    return this.firestore.delete(this.tenantContext.collections.SPONSORS, id);
  }

  /**
   * Persists the given order: each sponsor's `order` is set to its index in
   * the array. Runs as a single batch write.
   */
  async reorder(sponsors: Sponsor[]): Promise<void> {
    const batch = writeBatch(this.firestore.db);
    sponsors.forEach((sponsor, index) => {
      if (sponsor.order === index) return;
      const ref = this.firestore.getDocumentRef(
        this.tenantContext.collections.SPONSORS,
        sponsor.id,
      );
      batch.update(ref as never, { order: index, updatedAt: new Date() });
    });
    return batch.commit();
  }

  private _compareOrder(a: Sponsor, b: Sponsor): number {
    const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    return (a.name ?? '').localeCompare(b.name ?? '');
  }
}
